<template>
  <MainLayout
    :initialDrawerExpanded="false"
    contentClass="pa-0"
    :has-sidebar="false"
  >
    <template #content>
      <div class="settings-page fill-height">

        <!-- Hero -->
        <div class="settings-hero" :style="heroStyle">
          <router-link to="/" class="settings-logo" aria-label="Accueil">
            <v-img
              :src="$theme.assets.logoDark || $theme.assets.logo"
              :height="$theme.layout.sidebarLogoHeight"
              :width="$theme.layout.sidebarLogoHeight * 4"
              contain
              position="left center"
            />
          </router-link>
        </div>

        <!-- Retour -->
        <div class="settings-topbar px-4 px-md-6 pt-3">
          <PageBack small class="settings-back ml-n3" />
        </div>

        <!-- Cards -->
        <div class="settings-cards px-4 px-md-6 pb-8">
        <v-row>
        <v-col cols="12" class="settings-header">
          <div class="d-flex align-center">
            <div class="avatar-circle mr-4">
              <span class="avatar-letter">{{ avatarLetter }}</span>
            </div>
            <div class="text-h6 font-weight-bold">{{ userName || 'Utilisateur' }}</div>
            <v-chip v-if="isStaff" x-small color="primary" class="ml-3">
              <v-icon x-small class="mr-1" color="white">mdi-account-hard-hat</v-icon>
              Staff
            </v-chip>
          </div>
        </v-col>
        <v-col cols="12" md="6">

          <!-- Compte -->
          <div class="settings-section-label overline grey--text text--darken-1 mt-6 mb-2 px-1">Compte</div>

          <v-card class="settings-card rounded-lg" outlined>
            <v-card-text class="pa-0">
              <div class="settings-row px-4 py-3">
                <div class="settings-row__icon">
                  <v-icon color="blue darken-1">mdi-account-outline</v-icon>
                </div>
                <div class="settings-row__body">
                  <div class="subtitle-2 font-weight-medium">Nom d'utilisateur</div>
                  <v-text-field
                    v-model="userName"
                    hide-details
                    disabled
                    dense
                    solo
                    flat
                    class="mt-1 settings-input"
                  />
                </div>
              </div>
              <v-divider />
              <div class="settings-row px-4 py-3">
                <div class="settings-row__icon">
                  <v-icon color="blue darken-1">mdi-form-textbox</v-icon>
                </div>
                <div class="settings-row__body d-flex" style="gap: 12px;">
                  <div style="flex: 1;">
                    <div class="subtitle-2 font-weight-medium">Prénom</div>
                    <v-text-field
                      :value="profile && profile.first_name"
                      hide-details
                      disabled
                      dense
                      solo
                      flat
                      class="mt-1 settings-input"
                    />
                  </div>
                  <div style="flex: 1;">
                    <div class="subtitle-2 font-weight-medium">Nom</div>
                    <v-text-field
                      :value="profile && profile.last_name"
                      hide-details
                      disabled
                      dense
                      solo
                      flat
                      class="mt-1 settings-input"
                    />
                  </div>
                </div>
              </div>
              <v-divider />
              <div class="settings-row px-4 py-3">
                <div class="settings-row__icon">
                  <v-icon color="blue darken-1">mdi-email-outline</v-icon>
                </div>
                <div class="settings-row__body">
                  <div class="subtitle-2 font-weight-medium">Adresse e-mail</div>
                  <v-text-field
                    :value="profile && profile.email"
                    hide-details
                    disabled
                    dense
                    solo
                    flat
                    class="mt-1 settings-input"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Organisation -->
          <template v-if="profile && profile.organisation_name">
            <div class="settings-section-label overline grey--text text--darken-1 mt-6 mb-2 px-1">Organisation</div>

            <v-card class="settings-card rounded-lg" outlined>
              <v-card-text class="pa-0">
                <div class="settings-row px-4 py-3">
                  <div class="settings-row__icon">
                    <div class="org-avatar" :style="orgAvatarStyle">
                      <span v-if="!profile.organisation_icon" class="org-avatar__letter">{{ orgInitial }}</span>
                    </div>
                  </div>
                  <div class="settings-row__body">
                    <div class="subtitle-2 font-weight-medium">{{ profile.organisation_name }}</div>
                    <div class="caption grey--text">Organisation</div>
                  </div>
                </div>
                <v-divider />
                <div class="settings-row px-4 py-3">
                  <div class="settings-row__icon">
                    <v-icon color="blue darken-1">mdi-account-group-outline</v-icon>
                  </div>
                  <div class="settings-row__body">
                    <div class="subtitle-2 font-weight-medium">Équipe</div>
                    <v-select
                      :value="profile.teams && profile.teams[0] && profile.teams[0].name"
                      :items="profile.teams ? profile.teams.map(t => t.name) : []"
                      hide-details
                      disabled
                      dense
                      solo
                      flat
                      class="mt-1 settings-input"
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </template>

        </v-col>
        <v-col cols="12" md="6">

          <!-- Plan de stock -->
          <div class="settings-section-label overline grey--text text--darken-1 mt-6 mb-2 px-1">Plan de stock</div>

          <v-card class="settings-card rounded-lg" outlined>
            <v-card-text class="pa-0">
              <div class="settings-row px-4 py-3">
                <div class="settings-row__icon">
                  <v-icon color="blue darken-1">mdi-warehouse</v-icon>
                </div>
                <div class="settings-row__body">
                  <div class="subtitle-2 font-weight-medium">Plan de stock par défaut</div>
                  <v-select
                    :value="stockPref.selected"
                    :items="stockPlanItems"
                    :loading="stockPrefSaving"
                    :disabled="stockPrefSaving || !stockPref.options.length"
                    :placeholder="stockPref.options.length ? '' : 'Aucun plan disponible'"
                    :hint="stockPlanHint"
                    persistent-hint
                    dense
                    solo
                    flat
                    class="mt-1 settings-input"
                    @change="onStockPlanChange"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Limites (staff only: end users never see offers/limits) -->
          <template v-if="isStaff">
            <div class="settings-section-label overline grey--text text--darken-1 mt-6 mb-2 px-1">Offre</div>
            <UserLimits />
          </template>

          <!-- Apparence -->
          <div class="settings-section-label overline grey--text text--darken-1 mt-6 mb-2 px-1">Apparence</div>

          <v-card class="settings-card rounded-lg mb-2" outlined>
            <v-card-text class="pa-0">
              <div class="settings-row px-4 py-3 d-flex align-center">
                <div class="settings-row__icon">
                  <v-icon :color="darkMode ? 'indigo lighten-2' : 'amber darken-2'">
                    {{ darkMode ? 'mdi-weather-night' : 'mdi-weather-sunny' }}
                  </v-icon>
                </div>
                <div class="settings-row__body flex-grow-1">
                  <div class="subtitle-2 font-weight-medium">Mode sombre</div>
                  <div class="caption grey--text">{{ darkMode ? 'Thème sombre activé' : 'Thème clair activé' }}</div>
                </div>
                <v-switch v-model="darkMode" hide-details dense inset class="mt-0 pt-0" />
              </div>
              <v-divider v-if="isStaff" />
              <div v-if="isStaff" class="settings-row px-4 py-3 d-flex align-center">
                <div class="settings-row__icon">
                  <v-icon color="primary">mdi-palette-outline</v-icon>
                </div>
                <div class="settings-row__body flex-grow-1">
                  <div class="subtitle-2 font-weight-medium">Thème</div>
                  <div class="caption grey--text">Couleurs, police et mise en page</div>
                </div>
                <v-select
                  v-model="themeId"
                  :items="themeOptions"
                  item-value="id"
                  item-text="label"
                  dense
                  outlined
                  hide-details
                  style="max-width: 180px"
                />
              </div>
              <v-divider />
              <div class="settings-row px-4 py-3 d-flex align-center">
                <div class="settings-row__icon">
                  <v-icon color="primary">mdi-table-eye</v-icon>
                </div>
                <div class="settings-row__body flex-grow-1">
                  <div class="subtitle-2 font-weight-medium">Détails du tableau quantitatif</div>
                  <div class="caption grey--text">
                    {{ showMaterialDetails ? 'Tous les codes et le besoin exprimé sont affichés' : 'Vue compacte : code PDS et désignation seulement' }}
                  </div>
                </div>
                <v-switch v-model="showMaterialDetails" hide-details dense inset class="mt-0 pt-0" />
              </div>
              <v-divider />
              <div class="settings-row px-4 py-3 d-flex align-center">
                <div class="settings-row__icon">
                  <v-icon color="primary">mdi-image-outline</v-icon>
                </div>
                <div class="settings-row__body flex-grow-1">
                  <div class="subtitle-2 font-weight-medium">Images des produits</div>
                  <div class="caption grey--text">
                    {{ showProductImages ? 'Les vignettes produit sont affichées (tableau quantitatif, plans de stock)' : 'Aucune vignette produit affichée' }}
                  </div>
                </div>
                <v-switch v-model="showProductImages" hide-details dense inset class="mt-0 pt-0" />
              </div>
            </v-card-text>
          </v-card>

          <!-- IA -->
          <div v-if="isStaff" class="settings-section-label overline grey--text text--darken-1 mt-6 mb-2 px-1">Intelligence Artificielle</div>

          <v-card v-if="isStaff" class="settings-card rounded-lg" outlined>
            <v-card-text class="pa-0">
              <div class="settings-row px-4 py-3 d-flex align-center">
                <div class="settings-row__icon">
                  <v-icon :color="useMock ? 'orange darken-1' : 'green darken-1'">
                    {{ useMock ? 'mdi-test-tube' : 'mdi-robot-outline' }}
                  </v-icon>
                </div>
                <div class="settings-row__body flex-grow-1">
                  <div class="subtitle-2 font-weight-medium">Source des réponses</div>
                  <div class="caption grey--text">
                    {{ useMock ? 'Réponses simulées — aucun appel API' : 'Appels réels à l\'API ChatGPT' }}
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-chip
                    x-small
                    :color="useMock ? 'orange lighten-4' : 'green lighten-4'"
                    :text-color="useMock ? 'orange darken-3' : 'green darken-3'"
                    class="mr-3 font-weight-medium"
                  >
                    {{ useMock ? 'Simulation' : 'Production' }}
                  </v-chip>
                  <v-switch v-model="useMock" hide-details dense inset class="mt-0 pt-0" />
                </div>
              </div>
            </v-card-text>
          </v-card>

        </v-col>
        </v-row>
        </div>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import UserLimits from '@/components/ui/UserLimits.vue'
import { theme, themeOptions, setTheme } from '@/theme'
import { fetchUser } from '@/services/accountApi'
import { fetchStockPlanPreference, saveStockPlanPreference } from '@/services/stockApi'

export default {
  name: 'SettingsView',
  components: { PageBack, MainLayout, UserLimits },
  data() {
    return {
      profile: null,
      stockPref: { selected: null, effective: null, options: [] },
      stockPrefSaving: false,
    }
  },
  async mounted() {
    try {
      this.profile = await fetchUser()
    } catch (e) {
      console.warn('Failed to load user profile', e)
    }
    try {
      this.stockPref = await fetchStockPlanPreference()
    } catch (e) {
      console.warn('Failed to load stock plan preference', e)
    }
  },
  methods: {
    async onStockPlanChange(planId) {
      this.stockPrefSaving = true
      try {
        this.stockPref = await saveStockPlanPreference(planId)
      } catch (e) {
        console.warn('Failed to save stock plan preference', e)
        this.stockPref = await fetchStockPlanPreference().catch(() => this.stockPref)
      } finally {
        this.stockPrefSaving = false
      }
    },
  },
  computed: {
    stockPlanItems() {
      const auto = { text: 'Automatique (utilisateur > équipe > organisation)', value: null }
      const options = this.stockPref.options.map(o => ({
        text: this.isStaff && o.owner_label ? `${o.name} — ${o.owner_label}` : o.name,
        value: o.id,
      }))
      return [auto, ...options]
    },
    stockPlanHint() {
      const eff = this.stockPref.effective
      return eff ? `Plan utilisé actuellement : ${eff.name}` : "Aucun plan de stock n'est configuré pour votre compte."
    },
    isStaff() { return this.$store.getters["settings/isStaff"]},
    organisationBanner() { return this.$store.getters['settings/organisationBanner'] },
    heroStyle() {
      if (this.organisationBanner) {
        return {
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.85) 100%), url(${this.organisationBanner})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      }
      return {}
    },
    avatarLetter() {
      return (this.userName || 'U').charAt(0).toUpperCase()
    },
    orgInitial() {
      return (this.profile && this.profile.organisation_name || 'O').charAt(0).toUpperCase()
    },
    orgAvatarStyle() {
      if (this.profile && this.profile.organisation_icon) {
        return { backgroundImage: `url(${this.profile.organisation_icon})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      }
      return {}
    },
    useMock: {
      get() { return this.$store.getters['settings/useMockApi'] },
      set(value) { this.$store.dispatch('settings/toggleMockApi', value) },
    },
    userName: {
      get() { return this.$store.getters['settings/userName'] },
      set(value) { this.$store.dispatch('settings/updateUserName', value) },
    },
    themeOptions: () => themeOptions,
    themeId: {
      get() { return theme.id },
      set(id) { setTheme(id, this.$vuetify) },
    },
    showMaterialDetails: {
      get() { return this.$store.getters['settings/showMaterialDetails'] },
      set(value) { this.$store.dispatch('settings/toggleMaterialDetails', value) },
    },
    showProductImages: {
      get() { return this.$store.getters['settings/showProductImages'] },
      set(value) { this.$store.dispatch('settings/toggleProductImages', value) },
    },
    darkMode: {
      get() { return this.$store.getters['settings/darkMode'] },
      set(value) { this.$store.dispatch('settings/toggleDarkMode', value) },
    },
  },
}
</script>

<style scoped>

.settings-hero {
  position: relative;
  background-image: linear-gradient(135deg, #1565c0 0%, #0288d1 100%);
  background-size: cover;
  background-position: center;
}

/* 60px strip: logo (left) and the fixed account button (right) share a line. */
.settings-logo {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  display: block;
  z-index: 1;
}

.settings-hero {
  height: 60px;
}

.settings-topbar,
.settings-cards {
  max-width: 1148px;
  margin: 0 auto;
}

.settings-back {
  text-transform: none;
  letter-spacing: normal;
  position: relative;
  z-index: 1;
}

.avatar-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--v-primary-base);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-letter {
  font-size: 20px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

.settings-section-label {
  font-size: 10px;
  letter-spacing: 0.12em;
}

.settings-card {
  border-color: #e0e4ea !important;
  overflow: hidden;
}

.settings-row {
  display: flex;
  align-items: center;
}

.settings-row__icon {
  width: 36px;
  flex-shrink: 0;
}

.settings-row__body {
  flex-grow: 1;
  min-width: 0;
}

.settings-input >>> .v-input__slot {
  background: transparent !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
}

.settings-input >>> input {
  font-size: 14px;
}

.org-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #dce8f8;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.org-avatar__letter {
  font-size: 13px;
  font-weight: 700;
  color: #1565c0;
  line-height: 1;
}
</style>
